import { OpError } from '../domain/projectOps';
import type { Animation, Channel, Character, Easing, Keyframe, SceneObject } from '../domain/schemas';
import { IDLE_SHAPE, JUMP_POSES, RUN_SWING, WALK_SWING, legSwing, r2, runShape, walkShape, type CycleShape, type Quarters } from './procedural/poses';
import { baseValue } from './timeline';

/**
 * Deterministic, editable animation presets (Phase 3B). A preset generates ordinary
 * keyframes for the channels it animates, replacing that object's existing tracks for
 * those channels only; everything it creates can be edited on the timeline afterwards.
 * Nothing is random: the same object, character and animation always give the same keys.
 *
 * Cyclic presets repeat a cycle (keys at every quarter cycle, eased in-out) from frame 0
 * until the end of the animation. Sizes adapt to the instance (distances are fractions
 * of its height) and limb swings adapt to the character's leg length. Walk, run and idle
 * pose the limbs in place; add position keys to travel.
 */
export type MotionPresetId = 'idle' | 'walk' | 'run' | 'jump' | 'float' | 'spin';

export const MOTION_PRESETS: Record<MotionPresetId, { label: string; description: string; characterOnly: boolean }> = {
  idle: { label: 'Idle breathing', description: 'Gentle breathing: torso rises, arms and head sway slightly', characterOnly: true },
  walk: { label: 'Walking', description: 'Walk cycle in place: legs and arms swing, the body bobs', characterOnly: true },
  run: { label: 'Running', description: 'Run cycle in place: wider, faster swings and a forward lean', characterOnly: true },
  jump: { label: 'Jumping', description: 'Crouch, leap up and land (characters also tuck limbs)', characterOnly: false },
  float: { label: 'Floating', description: 'Slow hover up and down with a slight tilt', characterOnly: false },
  spin: { label: 'Spinning', description: 'Full turns around the anchor at a steady speed', characterOnly: false },
};
export const MOTION_PRESET_IDS = Object.keys(MOTION_PRESETS) as MotionPresetId[];

type Tracks = Partial<Record<Channel, Keyframe[]>>;

/** Keys for a cyclic motion: one value per quarter cycle (phase 0, ¼, ½, ¾), repeated. */
function cycle(a: Animation, cycleFrames: number, quarters: CycleShape, easing: Easing = 'ease-in-out'): Tracks {
  const out: Tracks = {};
  const step = Math.max(1, cycleFrames / 4);
  for (const [channel, values] of Object.entries(quarters) as [Channel, Quarters][]) {
    const keys: Keyframe[] = [];
    for (let i = 0; ; i++) {
      const frame = Math.round(i * step);
      if (frame > a.durationFrames - 1) break;
      if (keys.length && keys[keys.length - 1]!.frame === frame) continue;
      keys.push({ frame, value: values[i % 4]!, easing });
    }
    out[channel] = keys;
  }
  return out;
}

const framesFor = (a: Animation, seconds: number) => Math.max(4, Math.round(seconds * a.fps));

/**
 * Keyframes a preset produces for an object in an animation. Throws OpError for a
 * character-only preset on a non-character.
 */
export function presetTracks(preset: MotionPresetId, object: SceneObject, character: Character | undefined, a: Animation): Tracks {
  const info = MOTION_PRESETS[preset];
  const isCharacter = object.type === 'character' && !!character;
  if (info.characterOnly && !isCharacter) throw new OpError(`“${info.label}” poses a character's limbs; select a character instance.`);
  const H = object.height;
  switch (preset) {
    case 'idle':
      return cycle(a, framesFor(a, 2.4), IDLE_SHAPE);
    case 'walk':
      return cycle(a, framesFor(a, 1), walkShape(character, legSwing(WALK_SWING, character)));
    case 'run':
      return cycle(a, framesFor(a, 0.6), runShape(character, legSwing(RUN_SWING, character)));
    case 'jump': {
      // One jump per 1.2 s: crouch (0), take off (¼, ease-out up), peak (½), land (¾, ease-in down).
      const n = framesFor(a, 1.2);
      const y0 = baseValue(object, 'y');
      const lift = r2(H * 0.6);
      const out = cycle(a, n, { y: [y0, r2(y0 - lift * 0.7), r2(y0 - lift), r2(y0 - lift * 0.4)] });
      // Rising segments ease out, falling ones ease in: a believable arc from four keys.
      out.y = out.y!.map((k, i) => ({ ...k, easing: i % 4 === 0 || i % 4 === 1 ? 'ease-out' : 'ease-in' }));
      if (isCharacter) Object.assign(out, cycle(a, n, JUMP_POSES));
      return out;
    }
    case 'float': {
      const y0 = baseValue(object, 'y');
      const r0 = baseValue(object, 'rotation');
      const amp = r2(H * 0.05);
      return cycle(a, framesFor(a, 3), { y: [y0, r2(y0 - amp), y0, r2(y0 + amp)], rotation: [r0, r2(r0 + 3), r0, r2(r0 - 3)] });
    }
    case 'spin': {
      // Steady turns (360° every 2 s), keyed once per turn plus the last frame.
      const turn = framesFor(a, 2);
      const r0 = baseValue(object, 'rotation');
      const keys: Keyframe[] = [];
      for (let f = 0; f < a.durationFrames; f += turn) keys.push({ frame: f, value: r2(r0 + (360 * f) / turn), easing: 'linear' });
      const last = a.durationFrames - 1;
      if (keys[keys.length - 1]!.frame !== last && last > 0) keys.push({ frame: last, value: r2(r0 + (360 * last) / turn), easing: 'linear' });
      return { rotation: keys };
    }
  }
}
